// The "Mailbox health" card (steps E57, E58; migration 20261126100000): what crm_mailbox_health returns, and the plain
// words the card shows for it. Kept free of React so the wording is tested on its own.

export interface BlocklistAnswer {
  provider: string;
  listed: boolean | null;
  code: string | null;
  meaning: string | null;
  error: string | null;
  checked_at: string;
}

export interface MailboxHold {
  reason: "complaints" | "blocklist";
  since: string;
  step: string;
  detail: Record<string, unknown>;
}

export interface MailboxHealthRow {
  mailbox_id: string;
  address: string;
  domain: string;
  provider: string;
  status: string;
  age_days: number;
  cap: number | null;
  sent_today: number | null;
  remaining: number | null;
  ramp: { next: { until_day: number; cap: number } | null; full_cap: number | null };
  since: string | null;
  sends: number;
  unsubscribes: number;
  negatives: number;
  complaints: number;
  unsubscribe_pct: number | null;
  negative_pct: number | null;
  complaint_pct: number | null;
  judged: boolean;
  breaches: string[];
  blocklists: BlocklistAnswer[];
  holds: MailboxHold[];
  paused: boolean;
  why: string[];
}

export interface MailboxHealthSettings {
  watch_complaints: boolean;
  unsubscribe_pct: number;
  negative_pct: number;
  complaint_pct: number;
  min_sends: number;
  window_days: number;
  bounce_pct: number;
  bounce_window_days: number;
}

/** The mailbox that sends: the first ready one, else the first warming one. Null while nothing can send. */
export function sendingMailbox(h: Pick<MailboxHealth, "mailboxes"> | null | undefined): MailboxHealthRow | null {
  const all = h?.mailboxes ?? [];
  return all.find((m) => m.status === "ready") ?? all.find((m) => m.status === "warming") ?? null;
}

export interface MailboxHealth {
  settings: MailboxHealthSettings;
  email_paused_by_bounces: boolean;
  bounce_pct: number | null;
  bounce_sent: number;
  mailboxes: MailboxHealthRow[];
}

export const LIST_NAMES: Record<string, string> = {
  abusix_dblack: "Abusix",
  spamhaus_dbl: "Spamhaus",
  surbl: "SURBL",
  uribl: "URIBL",
  google_postmaster: "Gmail Postmaster Tools",
};

/** "Day 10 of warming up: 15 a day until day 14" or "Warmed up: 25 a day". */
export function rampText(m: Pick<MailboxHealthRow, "age_days" | "cap" | "ramp" | "status">): string {
  if (m.status !== "ready") return `Not sending yet (${m.status})`;
  const cap = m.cap ?? 0;
  if (m.ramp.next) return `Day ${m.age_days + 1} of warming up: ${cap} a day until day ${m.ramp.next.until_day}`;
  return `Warmed up: ${cap} a day`;
}

/** "Sent 12 of 15 in the last 24 hours" (the cap is a rolling 24 hours), or, while held, that cold email has stopped
 * and replies still go. */
export function todayText(m: Pick<MailboxHealthRow, "cap" | "sent_today" | "paused" | "status">): string {
  if (m.status !== "ready") return "None today";
  const sent = m.sent_today ?? 0;
  if (m.paused) return `Paused: ${sent} sent in the last 24 hours, no cold email until resumed (replies still go)`;
  return `Sent ${sent} of ${m.cap ?? 0} in the last 24 hours`;
}

/** A rate against its limit: "1.5% (limit 2%)", or "Not judged until 40 emails" below the minimum. */
export function rateText(pct: number | null, limit: number, judged: boolean, minSends: number, count: number): string {
  if (!judged) return count ? `${count} so far; judged after ${minSends} emails` : `None; judged after ${minSends} emails`;
  return `${fmtPct(pct ?? 0)} (limit ${fmtPct(limit)})`;
}

export function fmtPct(n: number): string {
  return `${Number.isInteger(n) ? n : n.toFixed(n < 1 ? 2 : 1).replace(/0+$/, "").replace(/\.$/, "")}%`;
}

export function overLimit(pct: number | null, limit: number, judged: boolean): boolean {
  return judged && pct != null && pct > limit;
}

/** The wording the owner sees (issues log item 26): what Ogma does, and the last check. Nothing until a check has run. */
export const BLOCKLIST_SERVICE = "Ogma checks your domain against the main blocklists every day";

/**
 * The blocklist line: null until any check has run; "Listed on Spamhaus: spam domain" when a list names the domain;
 * otherwise the service line with the date of the last check and whether it was clear.
 */
export function blocklistText(answers: BlocklistAnswer[], fmt: (iso: string) => string = (iso) => iso.slice(0, 10)): { text: string; tone: "ok" | "bad" | "muted" } | null {
  if (!answers.length) return null;
  const listed = answers.filter((a) => a.listed);
  if (listed.length) {
    return { text: listed.map((a) => `Listed on ${LIST_NAMES[a.provider] ?? a.provider}${a.meaning ? `: ${a.meaning}` : ""}`).join("; "), tone: "bad" };
  }
  const last = answers.map((a) => a.checked_at).filter(Boolean).sort().pop();
  const clear = answers.some((a) => a.listed === false);
  const when = last ? ` (last check ${fmt(last)}${clear ? ": clear" : ": no answer, trying again tomorrow"})` : "";
  return { text: `${BLOCKLIST_SERVICE}${when}.`, tone: clear ? "ok" : "muted" };
}

export function holdText(h: MailboxHold): string {
  if (h.reason === "blocklist") {
    const d = h.detail as { domain?: string; provider?: string; meaning?: string };
    return `Paused because ${d.domain ?? "the domain"} is on ${LIST_NAMES[d.provider ?? ""] ?? d.provider ?? "a blocklist"}${d.meaning ? ` (${d.meaning})` : ""}.`;
  }
  const b = ((h.detail as { breaches?: string[] }).breaches ?? [])[0];
  return `Paused for complaint signals${b ? `: ${b}` : ""}.`;
}

/** 4.10 Shown on Drafts to approve and Today while no mailbox can send: approving still works, sending waits. */
export const NO_MAILBOX_TEXT = "Approved emails wait until you connect a mailbox";
/** Where the owner connects one: Settings › Sending › Mailboxes. */
export const CONNECT_MAILBOX_PATH = "/settings/sending";

/**
 * Whether a mailbox is connected and ready to send, and its provider (the first ready one). `connected` is null when
 * the health could not be read, so no notice is shown on a guess.
 */
export function mailboxState(h: Pick<MailboxHealth, "mailboxes"> | null | undefined): { connected: boolean | null; provider: string | null } {
  if (!h || !Array.isArray(h.mailboxes)) return { connected: null, provider: null };
  const ready = h.mailboxes.find((m) => m.status === "ready");
  return { connected: Boolean(ready), provider: ready?.provider ?? h.mailboxes[0]?.provider ?? null };
}
